import axios from "axios";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import "./Form.css";
import Swal from "sweetalert2";
import heic2any from "heic2any";

const AddTeamMemberForm = ({ onClose }) => {
  const [title, setTitle] = useState("");
  const [linkedInLink, setLinkedInLink] = useState("");
  const [facebookLink, setFacebookLink] = useState("");
  const [instagramLink, setInstagramLink] = useState("");
  const [twitterLink, setTwitterLink] = useState("");
  const [whatsAppNumber, setWhatsAppNumber] = useState("");
  const [emailAddress, setEmailAddress] = useState("");
  const [designation, setDesignation] = useState("");
  const [singleImage, setSingleImage] = useState(null);
  const [showWebsite, setShowWebsite] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isLoadingSingleImage, setIsLoadingSingleImage] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const formData = new FormData();
    formData.append("title", title);
    formData.append("designation", designation);
    formData.append("linkedInLink", linkedInLink);
    formData.append("facebookLink", facebookLink);
    formData.append("instagramLink", instagramLink);
    formData.append("twitterLink", twitterLink);
    formData.append("whatsAppNumber", whatsAppNumber);
    formData.append("emailAddress", emailAddress);
    formData.append("showWebsite", showWebsite);

    if (singleImage) {
      formData.append("singleImage", singleImage);
    }

    try {
      const response = await axios.post(
        "https://server-gs.vercel.app/upload/team-member",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      
      Swal.fire({
        title: "Success!",
        text: "Team member added successfully.",
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      }).then(() => {
        onClose();
        // Reset form
        setTitle("");
        setDesignation("");
        setLinkedInLink("");
        setFacebookLink("");
        setInstagramLink("");
        setTwitterLink("");
        setWhatsAppNumber("");
        setEmailAddress("");
        setSingleImage(null);
        setShowWebsite(false);
      });
    } catch (error) {
      console.error("Error adding team member:", error);
      toast.error("Failed to add team member");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      await handleFile(file, setIsLoadingSingleImage, setSingleImage);
      e.dataTransfer.clearData();
    }
  };

  const handleFileChange = async (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      await handleFile(file, setIsLoadingSingleImage, setSingleImage);
    }
  };

  const handleFile = async (file, setIsLoading, setImageState) => {
    setIsLoading(true);
    try {
      if (
        file.name.toLowerCase().endsWith(".heic") ||
        file.name.toLowerCase().endsWith(".heif")
      ) {
        const convertedBlob = await heic2any({
          blob: file,
          toType: "image/jpeg",
          quality: 0.8,
        });

        const convertedFile = new File(
          [convertedBlob],
          file.name.replace(/\.heic$|\.heif$/i, ".jpeg"),
          { type: "image/jpeg" }
        );
        setImageState(convertedFile);
      } else {
        setImageState(file);
      }
    } catch (error) {
      console.error("Error processing file:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="p-6 ">
      <form onSubmit={handleSubmit} encType="multipart/form-data">
        <div className="flex flex-col gap-6">
          {/* Basic Info Section */}
          <div className="grid grid-cols-2 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-md font-medium text-gray-900 mb-1">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="block w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-md font-medium text-gray-900 mb-1">
                Designation <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={designation}
                onChange={(e) => setDesignation(e.target.value)}
                className="block w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
          </div>

          {/* Contact Info Section */}
          <div className="grid grid-cols-3 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-md font-medium text-gray-900 mb-1">
                LinkedIn URL
              </label>
              <input
                type="url"
                value={linkedInLink}
                onChange={(e) => setLinkedInLink(e.target.value)}
                className="block w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                placeholder="https://linkedin.com/in/username"
              />
            </div>
            <div>
              <label className="block text-md font-medium text-gray-900 mb-1">
                Facebook URL
              </label>
              <input
                type="url"
                value={facebookLink}
                onChange={(e) => setFacebookLink(e.target.value)}
                className="block w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                placeholder="https://facebook.com/username"
              />
            </div>
            <div>
              <label className="block text-md font-medium text-gray-900 mb-1">
                Instagram URL
              </label>
              <input
                type="url"
                value={instagramLink}
                onChange={(e) => setInstagramLink(e.target.value)}
                className="block w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                placeholder="https://instagram.com/username"
              />
            </div>
            <div>
              <label className="block text-md font-medium text-gray-900 mb-1">
                Twitter URL
              </label>
              <input
                type="url"
                value={twitterLink}
                onChange={(e) => setTwitterLink(e.target.value)}
                className="block w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                placeholder="https://twitter.com/username"
              />
            </div>
            <div>
              <label className="block text-md font-medium text-gray-900 mb-1">
                WhatsApp Number
              </label>
              <input
                type="tel"
                value={whatsAppNumber}
                onChange={(e) => setWhatsAppNumber(e.target.value)}
                className="block w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                placeholder="+1234567890"
              />
            </div>
            <div>
              <label className="block text-md font-medium text-gray-900 mb-1">
                Email Address
              </label>
              <input
                type="email"
                value={emailAddress}
                onChange={(e) => setEmailAddress(e.target.value)}
                className="block w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                placeholder="member@example.com"
              />
            </div>
          </div>

          {/* Image Upload Section */}
          <div>
            <label className="block text-md font-medium text-gray-900 mb-2">
              Profile Picture <span className="text-red-500">*</span>
            </label>
            <div
              className={`relative border-2 border-dashed rounded-xl p-6 h-44 flex flex-col items-center justify-center transition-all ${
                isDragging
                  ? "border-blue-500 bg-blue-50"
                  : "border-gray-300 hover:border-gray-400 bg-gray-50"
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              {isLoadingSingleImage ? (
                <div className="flex flex-col items-center justify-center space-y-3">
                  <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  <p className="text-md text-gray-500">Uploading image...</p>
                </div>
              ) : singleImage ? (
                <div className="relative w-full h-full group">
                  <img
                    src={URL.createObjectURL(singleImage)}
                    alt="Preview"
                    className="w-full h-full object-contain rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={() => setSingleImage(null)}
                    className="absolute top-2 right-2 p-1 bg-white/90 rounded-full shadow-md hover:bg-white transition-all opacity-0 group-hover:opacity-100"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 text-red-600"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </div>
              ) : (
                <div className="text-center">
                  <svg
                    className="mx-auto h-12 w-12 text-gray-400"
                    stroke="currentColor"
                    fill="none"
                    viewBox="0 0 48 48"
                    aria-hidden="true"
                  >
                    <path
                      d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <div className="mt-2 flex text-md text-gray-600">
                    <label
                      htmlFor="fileInput"
                      className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500"
                    >
                      <span>Upload a file</span>
                      <input
                        id="fileInput"
                        type="file"
                        className="sr-only"
                        onChange={handleFileChange}
                        accept="image/*, .heic, .heif"
                      />
                    </label>
                    <p className="pl-1">or drag and drop</p>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    PNG, JPG, GIF up to 5MB
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Show Website Toggle */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="showWebsite"
              checked={showWebsite}
              onChange={() => setShowWebsite(!showWebsite)}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label
              htmlFor="showWebsite"
              className="ml-3 text-md font-medium text-gray-900"
            >
              Show on website
            </label>
          </div>

          {/* Submit Button */}
          <div className="pt-4">
            <button
              type="submit"
              disabled={isLoading}
              className={`w-full px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                isLoading ? "opacity-75 cursor-not-allowed" : ""
              }`}
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Processing...
                </span>
              ) : (
                "Add Team Member"
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AddTeamMemberForm;